import { TranslationsContext } from '@teliaee/telia-translate';
import { saveAs } from 'file-saver';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Button, Grid, GridColumn, Heading, Spinner, Pagination, PaginationNumber, Search, Separator, Text } from 'telia-front-react';
import GaDataStore from '../../stores/GaDataStore';
import { ProposalModalProps } from '../OfferInterfaces';
import { DOWNLOAD_EXCEL_GA } from './ProposalsGaEvents';
import MobProposalTable from './MobProposalTable';
import VolteProposalTable from './VolteProposalTable';
import { ProposalType } from '../../stores/ProposalStore';

interface ProposalModalState {
  [key: string]: ProposalModalStateByType;
}

interface ProposalModalStateByType {
  perPage: number;
  currentPage: number;
}

@observer
class ProposalContent extends React.Component<ProposalModalProps, ProposalModalState> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;

  private getStateByType(type: ProposalType, perPage?: number, currentPage?: number) {
    return { [type]: { perPage, currentPage } };
  }

  constructor(props: ProposalModalProps) {
    super(props);
    this.state = { ...this.getStateByType('MOB', 5, 1), ...this.getStateByType('VOLTE', 5, 1) };
  }

  public componentDidMount() {
    if (!this.props.isModal) {
      this.props.personalOffersStore.subscribe();
    }
  }

  public componentWillUnmount() {
    if (!this.props.isModal) {
      this.props.personalOffersStore.unsubscribe();
    }
  }

  private getRecommendationsByType(type: ProposalType) {
    const { proposalStore } = this.props;
    if (type === 'VOLTE') {
      return proposalStore.filteredRecommendations.volteRecommendations;
    }
    if (type === 'MOB') {
      return proposalStore.filteredRecommendations.mobRecommendations;
    }
  }

  private downloadExcel = async (type: ProposalType) => {
    GaDataStore.pushWithoutCmsData(DOWNLOAD_EXCEL_GA);
    const blobType: string = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const workbook = await this.props.proposalStore.fetchMobileRecommendationsExcel(type);
    if (workbook) {
      const blob = new Blob([workbook], { type: blobType });
      saveAs(blob, `${type === 'MOB' ? 'mobiilisoovitused' : 'volte_aktiveerimine'}.xls`);
    }
  };

  private paginationNumberItems = (type: ProposalType) => {
    const setPerPageByType = (perPage: number) => this.setState({ [type]: { ...this.state[type], perPage } });
    return [
      {
        current: this.state[type].perPage === 5,
        label: '5',
        url: '#perpage5',
        onClick: () => setPerPageByType(5)
      },
      {
        current: this.state[type].perPage === 10,
        label: '10',
        url: '#perpage10',
        onClick: () => setPerPageByType(10)
      },
      {
        current: this.state[type].perPage === 25,
        label: '25',
        url: '#perpage25',
        onClick: () => setPerPageByType(25)
      },
      {
        current: this.state[type].perPage === 50,
        label: '50',
        url: '#perpage50',
        onClick: () => setPerPageByType(50)
      }
    ];
  };

  private getProposalContentTable(type: ProposalType) {
    const { proposalStore, initialState } = this.props;
    const translate = this.context.translate;

    return (
      <Text>
        <Heading element={'h1'} className={'h4 text-center'}>
          {translate(`proposal.${type === 'VOLTE' ? 'volteRecommendations' : 'mobileRecommendations'}.modal.title`)}
        </Heading>
        <Separator spacing={'xxsmall'} color={'invisible'} />
        <Grid>
          <GridColumn width={['md-4']} />
          <GridColumn align={['middle-xs']} width={['xs-12', 'md-4']}>
            <Search
              id={'recommendations-search'}
              value={proposalStore.searchKeys && proposalStore.searchKeys[type === 'MOB' ? 'mobKey' : 'volteKey']}
              onChange={(value) => proposalStore.updateSearchValue(value, type)}
              label={translate('proposal.mobileRecommendations.modal.search')}
              onSubmit={(e) => {
                e.preventDefault();
                type === 'MOB' ? proposalStore.searchMobRecs() : proposalStore.searchVolteRecs();
              }}
            />
          </GridColumn>
          <GridColumn align={['middle-xs']} width={['xs-12', 'md-4']} className={'text-right'}>
            {!initialState?.isMyTeliaUserAgent && (
              <Button
                iconLeft={'#file-xls'}
                verticalAlign={'middle'}
                text={translate('proposal.mobileRecommendations.modal.downloadExcel')}
                link={true}
                id={'download-excel'}
                isLoading={proposalStore.isGeneratingExcel && proposalStore.excelType === type}
                onClick={() => this.downloadExcel(type)}
                disabled={this.getRecommendationsByType(type).length === 0}
              />
            )}
          </GridColumn>
        </Grid>
        {this.getRecommendationsByType(type).length > 5 && (
          <>
            <Grid align={['center-xs']}>
              <GridColumn width={['max']}>
                <PaginationNumber
                  color="light"
                  items={this.paginationNumberItems(type)}
                  textLeft={translate('mobileRecommendations.pagination.left')}
                  textRight={translate('mobileRecommendations.pagination.right')}
                />
              </GridColumn>
            </Grid>
          </>
        )}
        <Separator spacing={'xxsmall'} color={'invisible'} />
        {type === 'MOB' ? <MobProposalTable {...this.state['MOB']} /> : <VolteProposalTable {...this.state['VOLTE']} />}
        {this.getRecommendationsByType(type).length > 5 && (
          <>
            <Grid align={['center-xs']}>
              <GridColumn width={['max']}>
                <Pagination
                  style="inverse"
                  onChange={(currentPage: number) => this.setState({ [type]: { ...this.state[type], currentPage } })}
                  pages={Math.ceil(this.getRecommendationsByType(type).length / this.state[type].perPage)}
                  currentPage={this.state[type].currentPage}
                />
              </GridColumn>
            </Grid>
          </>
        )}
      </Text>
    );
  }

  public render() {
    const { proposalStore } = this.props;

    if (proposalStore.proposalsFetching) {
      return <Spinner />;
    }

    if (!proposalStore.hasProposals) {
      return this.props.noRecommendationsContent || null;
    }

    return (
      <>
        {proposalStore.hasMobProposals && this.getProposalContentTable('MOB')}
        {proposalStore.hasVolteProposals && proposalStore.hasMobProposals && <Separator spacing={'large'} weight={'thin'} fullWidth={true} />}
        {proposalStore.hasVolteProposals && this.getProposalContentTable('VOLTE')}
      </>
    );
  }
}

export default inject('proposalStore', 'personalOffersStore', 'initialState')(ProposalContent);
